import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ToastService } from '../../services/toast.service';
import {
  WeightEstimatorService,
  WeightEstimatorRun,
  WeightEstimatorVendor
} from '../../services/weight-estimator.service';

const DEFAULT_PARAMS = {
  sampleSize: 5000,
  quantile: 0.75,
  safetyAddLbs: 0.5,
  priceFloor: true,
  treatOneAsMissing: false,
  dupAgreementPct: 25,
  prefixMinLen: 4,
  prefixMaxLen: 12,
  prefixMinNeighbors: 3,
  sizeNearestK: 8,
  textMinShared: 3,
  textTopK: 25,
  textMinNeighbors: 5,
  typeMinSamples: 10,
  maxWeightCap: 3000,
  dupFamilyFloorPct: 25,
  sizeFit: true,
  sizeFitMinCorrelation: 0.7,
  sizeFitMinSamples: 5,
  bimodalRatio: 3,
  typeSanityBand: true,
  freightThresholdLbs: 70,
  tiers: { weightTag: true, duplicate: true, prefix: true, descSize: true, text: true, type: true }
};

@Component({
  selector: 'app-weight-estimator',
  templateUrl: './weight-estimator.component.html',
  styleUrls: ['./weight-estimator.component.scss']
})
export class WeightEstimatorComponent implements OnInit, OnDestroy {
  public vendors: WeightEstimatorVendor[] = [];
  public filteredVendors: WeightEstimatorVendor[] = [];
  public vendorFilter = '';
  public selectedVendor: string | null = null;

  public params: any = JSON.parse(JSON.stringify(DEFAULT_PARAMS));

  public runs: WeightEstimatorRun[] = [];
  public activeRun: WeightEstimatorRun | null = null;
  public runInProgress = false;
  public progress = { done: 0, total: 0, phase: '' };

  public results: any[] = [];
  public resultsTotal = 0;
  public resultsPage = 0;
  public resultsLimit = 100;
  public resultsFilter: { under?: boolean; tier?: string } = {};

  public estimates: any[] = [];
  public estimatesTotal = 0;
  public estimatesPage = 0;
  public estimatesLimit = 100;
  public estimatesFilter: { status?: string; tier?: string; minConfidence?: number; sort?: string } = { status: 'proposed', sort: 'risk' };
  public presetLoaded = false;
  public misses: any[] = [];
  public estimateCounts: any = {};
  public selectedEstimateIds = new Set<string>();

  public loading = false;
  public detailRow: any = null;
  public showAdvanced = false;
  public missesOpen = false;
  // null = follow the stage (auto-collapse once reviewing); true/false = user override
  private tuneCollapsedManual: boolean | null = null;
  private subs: Subscription[] = [];

  constructor(
    private weightEstimatorService: WeightEstimatorService,
    private toastService: ToastService
  ) {}

  // backend-provided defaults (single source of truth); the local constant is
  // only the fallback until the fetch lands
  private defaults: any = JSON.parse(JSON.stringify(DEFAULT_PARAMS));

  ngOnInit(): void {
    this.loadVendors();
    this.weightEstimatorService.getDefaults().subscribe({
      next: d => {
        if (d) {
          this.defaults = d;
          if (!this.selectedVendor) this.params = JSON.parse(JSON.stringify(d));
        }
      },
      error: () => { /* fallback constant stays */ }
    });
    this.subs.push(this.weightEstimatorService.events$.subscribe(msg => this.onWsEvent(msg)));
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  // ---------- vendors ----------

  loadVendors(): void {
    this.loading = true;
    this.weightEstimatorService.getVendors().subscribe({
      next: vendors => {
        this.vendors = vendors;
        this.applyVendorFilter();
        this.loading = false;
      },
      error: () => {
        this.toastService.error('Failed to load vendors');
        this.loading = false;
      }
    });
  }

  applyVendorFilter(): void {
    const f = this.vendorFilter.trim().toLowerCase();
    this.filteredVendors = f
      ? this.vendors.filter(v => v.vendor.toLowerCase().includes(f))
      : this.vendors;
  }

  get selectedVendorInfo(): WeightEstimatorVendor | undefined {
    return this.vendors.find(v => v.vendor === this.selectedVendor);
  }

  onVendorChange(): void {
    this.activeRun = null;
    this.results = [];
    this.tuneCollapsedManual = null;
    this.missesOpen = false;
    this.loadRuns();
    this.loadEstimates(0);
    this.loadPreset();
    this.loadMisses();
  }

  // ---------- per-vendor presets ----------

  loadPreset(): void {
    if (!this.selectedVendor) return;
    this.presetLoaded = false;
    this.weightEstimatorService.getPreset(this.selectedVendor).subscribe(preset => {
      if (preset && preset.params) {
        this.params = { ...JSON.parse(JSON.stringify(this.defaults)), ...preset.params, tiers: { ...this.defaults.tiers, ...(preset.params.tiers || {}) } };
        this.presetLoaded = true;
      } else {
        this.params = JSON.parse(JSON.stringify(this.defaults));
      }
    });
  }

  savePreset(): void {
    if (!this.selectedVendor) return;
    this.weightEstimatorService.savePreset(this.selectedVendor, this.params).subscribe({
      next: () => {
        this.presetLoaded = true;
        this.toastService.success(`Preset saved for ${this.selectedVendor} — auto-loads whenever this vendor is selected`);
      },
      error: () => this.toastService.error('Failed to save preset')
    });
  }

  loadMisses(): void {
    if (!this.selectedVendor) return;
    this.weightEstimatorService.getMisses(this.selectedVendor).subscribe(rows => this.misses = rows || []);
  }

  resetParams(): void {
    this.params = JSON.parse(JSON.stringify(this.defaults));
  }

  // ---------- runs ----------

  loadRuns(): void {
    if (!this.selectedVendor) return;
    this.weightEstimatorService.getRuns(this.selectedVendor).subscribe(runs => {
      this.runs = runs;
      // Only treat a recent 'running' run as in-progress: the backend marks
      // orphaned runs failed at boot, but a run that just lost its WS message
      // shouldn't brick the buttons forever either.
      const staleCutoff = Date.now() - 30 * 60 * 1000;
      const running = runs.find(r => r.status === 'running' && new Date(r.started_at).getTime() > staleCutoff);
      if (running) {
        this.runInProgress = true;
        this.activeRun = running;
      } else {
        this.runInProgress = false;
      }
    });
  }

  startBacktest(): void {
    if (!this.selectedVendor || this.runInProgress) return;
    this.runInProgress = true;
    this.progress = { done: 0, total: 0, phase: 'starting' };
    this.weightEstimatorService.startBacktest(this.selectedVendor, this.params).subscribe({
      next: res => {
        this.toastService.info('Backtest started');
        this.loadRuns();
      },
      error: () => {
        this.runInProgress = false;
        this.toastService.error('Failed to start backtest');
      }
    });
  }

  startGenerate(): void {
    if (!this.selectedVendor || this.runInProgress) return;
    if (!confirm(`Generate estimates for every ${this.selectedVendor} product with no vendor weight?\n\nNothing is written to products — proposals go to the review queue.`)) return;
    this.runInProgress = true;
    this.progress = { done: 0, total: 0, phase: 'starting' };
    this.weightEstimatorService.startGenerate(this.selectedVendor, this.params).subscribe({
      next: () => {
        this.toastService.info('Estimate generation started');
        this.loadRuns();
      },
      error: () => {
        this.runInProgress = false;
        this.toastService.error('Failed to start estimate generation');
      }
    });
  }

  openRun(run: WeightEstimatorRun): void {
    this.activeRun = run;
    this.params = { ...JSON.parse(JSON.stringify(this.defaults)), ...(run.params || {}) };
    this.results = [];
    this.resultsPage = 0;
    if (run.kind === 'backtest' && run.status === 'complete') {
      this.loadResults(0);
    }
  }

  private onWsEvent(msg: any): void {
    // WS events are broadcast to every browser — only react to events for the
    // vendor THIS user is looking at, or another admin's run hijacks the
    // scorecard, params, and progress bar
    const eventVendor = msg.payload?.vendor;
    if (!eventVendor || eventVendor !== this.selectedVendor) return;
    if (msg.type === 'progress') {
      this.progress = { done: msg.payload.done, total: msg.payload.total, phase: msg.payload.phase };
    } else if (msg.type === 'run-complete') {
      this.runInProgress = false;
      this.toastService.success('Run complete');
      this.loadRuns();
      this.loadEstimates(0);
      if (msg.payload && msg.payload.run_id) {
        this.weightEstimatorService.getRun(msg.payload.run_id).subscribe(run => this.openRun(run));
      }
    } else if (msg.type === 'run-failed') {
      this.runInProgress = false;
      this.toastService.error('Run failed — see run history for the error');
      this.loadRuns();
    }
  }

  // ---------- backtest results drill-down ----------

  loadResults(page: number): void {
    if (!this.activeRun) return;
    this.resultsPage = page;
    this.weightEstimatorService
      .getRunResults(this.activeRun._id, page, this.resultsLimit, this.resultsFilter)
      .subscribe(res => {
        this.results = res.rows;
        this.resultsTotal = res.total;
      });
  }

  toggleUnderFilter(): void {
    this.resultsFilter.under = !this.resultsFilter.under;
    this.loadResults(0);
  }

  setResultsTier(tier: string): void {
    this.resultsFilter.tier = tier || undefined;
    this.loadResults(0);
  }

  // ---------- review queue ----------

  loadEstimates(page: number): void {
    if (!this.selectedVendor) return;
    this.estimatesPage = page;
    this.selectedEstimateIds.clear();
    this.weightEstimatorService
      .getEstimates(this.selectedVendor, page, this.estimatesLimit, this.estimatesFilter)
      .subscribe(res => {
        this.estimates = res.rows;
        this.estimatesTotal = res.total;
        this.estimateCounts = {};
        for (const c of res.counts || []) {
          this.estimateCounts[c._id] = c.n;
        }
      });
  }

  setEstimatesStatus(status: string): void {
    this.estimatesFilter.status = status || undefined;
    this.loadEstimates(0);
  }

  setEstimatesSort(sort: string): void {
    this.estimatesFilter.sort = sort || undefined;
    this.loadEstimates(0);
  }

  setMinConfidence(value: any): void {
    const n = Number(value);
    this.estimatesFilter.minConfidence = n > 0 ? Math.min(n, 1) : undefined;
    this.loadEstimates(0);
  }

  nearFreightThreshold(est: any): boolean {
    const threshold = this.params.freightThresholdLbs || 70;
    return Math.abs((est.est_weight || 0) - threshold) <= 25;
  }

  toggleEstimate(id: string): void {
    if (this.selectedEstimateIds.has(id)) this.selectedEstimateIds.delete(id);
    else this.selectedEstimateIds.add(id);
  }

  toggleAllEstimates(): void {
    if (this.selectedEstimateIds.size === this.estimates.length) {
      this.selectedEstimateIds.clear();
    } else {
      this.estimates.forEach(e => this.selectedEstimateIds.add(e._id));
    }
  }

  reviewSelected(action: 'approve' | 'reject'): void {
    const ids = [...this.selectedEstimateIds];
    if (!ids.length) return;
    this.weightEstimatorService.reviewEstimates({ ids, action }).subscribe({
      next: res => {
        this.toastService.success(`${res.modified} estimate(s) ${action}d`);
        this.loadEstimates(this.estimatesPage);
      },
      error: () => this.toastService.error('Review action failed')
    });
  }

  reviewOne(est: any, action: 'approve' | 'reject'): void {
    this.weightEstimatorService.reviewEstimates({ ids: [est._id], action }).subscribe({
      next: () => this.loadEstimates(this.estimatesPage),
      error: () => this.toastService.error('Review action failed')
    });
  }

  reviewAllFiltered(action: 'approve' | 'reject'): void {
    if (!this.selectedVendor) return;
    const scope = this.estimatesFilter.tier ? `tier "${this.estimatesFilter.tier}"` : 'ALL tiers';
    if (!confirm(`${action === 'approve' ? 'Approve' : 'Reject'} every ${this.estimatesFilter.status || 'proposed'} estimate for ${this.selectedVendor} (${scope})?`)) return;
    this.weightEstimatorService.reviewEstimates({
      action,
      vendor: this.selectedVendor,
      filter: {
        status: this.estimatesFilter.status || 'proposed',
        tier: this.estimatesFilter.tier,
        minConfidence: this.estimatesFilter.minConfidence
      }
    }).subscribe({
      next: res => {
        this.toastService.success(`${res.modified} estimate(s) ${action}d`);
        this.loadEstimates(0);
      },
      error: () => this.toastService.error('Bulk review failed')
    });
  }

  revert(): void {
    if (!this.selectedVendor) return;
    const released = this.estimateCounts['released'] || 0;
    if (!released) {
      this.toastService.info('Nothing has been released for this vendor');
      return;
    }
    if (!confirm(`Revert ALL ${released} released estimate(s) for ${this.selectedVendor}?\n\nEach product goes back to its original placeholder weight through the normal save pipeline (push + sync restores Shopify). Products that received a real vendor weight since release are never touched.`)) return;
    this.weightEstimatorService.revertEstimates(this.selectedVendor).subscribe({
      next: res => {
        this.toastService.success(`Reverted ${res.reverted} weight(s)` + (res.skipped ? `, skipped ${res.skipped} (vendor weight arrived — kept)` : ''));
        this.loadEstimates(0);
      },
      error: () => this.toastService.error('Revert failed')
    });
  }

  release(): void {
    if (!this.selectedVendor) return;
    const approved = this.estimateCounts['approved'] || 0;
    if (!approved) {
      this.toastService.info('No approved estimates to release');
      return;
    }
    if (!confirm(`Release ${approved} approved estimate(s) for ${this.selectedVendor}?\n\nThis writes the estimated weights into staging through the normal save pipeline. Products that received a real vendor weight since approval are skipped automatically.`)) return;
    this.weightEstimatorService.releaseEstimates(this.selectedVendor).subscribe({
      next: res => {
        this.toastService.success(`Released ${res.released} weight(s)` + (res.skipped ? `, skipped ${res.skipped} (vendor weight arrived)` : ''));
        this.loadEstimates(0);
      },
      error: () => this.toastService.error('Release failed')
    });
  }

  // ---------- stage rail (presentational only) ----------

  /** Most recent completed backtest run for the selected vendor. */
  get lastBacktest(): WeightEstimatorRun | undefined {
    return this.completedRuns.find(r => r.kind === 'backtest');
  }

  /** Most recent completed generate run for the selected vendor. */
  get lastGenerate(): WeightEstimatorRun | undefined {
    return this.completedRuns.find(r => r.kind === 'generate');
  }

  private get completedRuns(): WeightEstimatorRun[] {
    return [...this.runs]
      .filter(r => r.status === 'complete')
      .sort((a, b) => (b.started_at || '').localeCompare(a.started_at || ''));
  }

  /** The lbs-based gate: err high, never under. */
  isPassing(m: any): boolean {
    return !!m && m.undersOver1Lb === 0 && !(m.underRate > 5);
  }

  get backtestPassed(): boolean {
    const bt = this.lastBacktest;
    return !!bt && this.isPassing(bt.metrics);
  }

  get proposedCount(): number { return this.estimateCounts['proposed'] || 0; }
  get approvedCount(): number { return this.estimateCounts['approved'] || 0; }
  get releasedCount(): number { return this.estimateCounts['released'] || 0; }

  /** 0 = no vendor picked yet; 1..4 = Backtest / Generate / Review / Release. */
  get currentStage(): number {
    if (!this.selectedVendor) return 0;
    if (this.approvedCount > 0 && this.proposedCount === 0) return 4;
    if (this.proposedCount > 0) return 3;
    if (this.backtestPassed) return 2;
    return 1;
  }

  get stages(): Array<{ n: number; label: string; status: string; state: string; anchor: string }> {
    const cur = this.currentStage;
    const bt = this.lastBacktest;
    const m = bt && bt.metrics;
    const gm = this.lastGenerate && this.lastGenerate.metrics;
    const info = this.selectedVendorInfo;
    const fmt = (n: number) => (n || 0).toLocaleString();
    const state = (n: number, attention = false) =>
      attention ? 'attention' : n < cur ? 'done' : n === cur ? 'current' : 'todo';
    return [
      {
        n: 1, label: 'Backtest', anchor: 'we-setup',
        status: !this.selectedVendor ? 'Score on known weights'
          : m ? (this.backtestPassed
            ? `Passed · ${m.underRate}% under`
            : `${fmt(m.undersOver1Lb)} unders > 1 lb`)
          : 'Not run yet',
        state: state(1, cur === 1 && !!m && !this.backtestPassed)
      },
      {
        n: 2, label: 'Generate', anchor: 'we-setup',
        status: gm ? `${fmt(gm.proposed)} of ${fmt(gm.gapTotal)} proposed`
          : info ? `${fmt(info.missing)} need weights`
          : 'Propose weights',
        state: state(2)
      },
      {
        n: 3, label: 'Review', anchor: 'we-review',
        status: this.selectedVendor
          ? `${fmt(this.proposedCount)} awaiting · ${fmt(this.approvedCount)} approved`
          : 'Approve or reject',
        state: state(3)
      },
      {
        n: 4, label: 'Release', anchor: 'we-review',
        status: this.approvedCount ? `${fmt(this.approvedCount)} ready`
          : this.releasedCount ? `${fmt(this.releasedCount)} released`
          : 'Write approved weights',
        state: state(4)
      }
    ];
  }

  trackStage(index: number): number {
    return index;
  }

  /** Plain-language "what to do now" line under the stage rail. */
  get nextStepHint(): string {
    const info = this.selectedVendorInfo;
    const fmt = (n: number) => (n || 0).toLocaleString();
    switch (this.currentStage) {
      case 0:
        return 'Pick a vendor to begin — the counts show how many products are missing a weight.';
      case 1:
        return this.lastBacktest
          ? 'The last backtest shows unders — raise Quantile or Safety margin (higher = errs heavier) and rerun until no item is under by more than 1 lb.'
          : `Run a backtest to score the algorithm against ${info ? fmt(info.control) + ' ' : ''}control products. Nothing is written to products.`;
      case 2:
        return `Backtest passed — generate estimates for the ${info ? fmt(info.missing) + ' ' : ''}products missing a weight. Proposals go to the review queue, not to products.`;
      case 3:
        return `Review the ${fmt(this.proposedCount)} proposed estimate(s) below — approve the ones you trust, reject the rest.`;
      case 4:
        return `Release the ${fmt(this.approvedCount)} approved estimate(s) to write real product weights.`;
      default:
        return '';
    }
  }

  /** Which run button is the recommended action right now. */
  get primaryAction(): 'backtest' | 'generate' {
    return this.currentStage === 2 ? 'generate' : 'backtest';
  }

  /** Tune & run card auto-collapses once the flow reaches Review, unless the user overrides. */
  get tuneCardCollapsed(): boolean {
    if (this.runInProgress) return false;
    return this.tuneCollapsedManual !== null ? this.tuneCollapsedManual : this.currentStage >= 3;
  }

  toggleTuneCard(): void {
    this.tuneCollapsedManual = !this.tuneCardCollapsed;
  }

  /** One-line summary shown while the Tune & run card is collapsed. */
  get tuneSummary(): string {
    const parts: string[] = [];
    parts.push(this.backtestPassed ? 'backtest passed' : this.lastBacktest ? 'backtest needs tuning' : 'backtest not run');
    if (this.paramsDirty) parts.push('custom params');
    else if (this.presetLoaded) parts.push('vendor preset');
    else parts.push('default params');
    return parts.join(' · ');
  }

  get paramsDirty(): boolean {
    return JSON.stringify(this.params) !== JSON.stringify(this.defaults);
  }

  scrollTo(anchor: string): void {
    const el = document.getElementById(anchor);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** Jump to the backtest results filtered to unders only. */
  inspectUnders(): void {
    this.resultsFilter.under = true;
    this.loadResults(0);
    setTimeout(() => this.scrollTo('we-results'));
  }

  onUndersMetric(m: any): void {
    if (m && m.undersOver1Lb > 0) this.inspectUnders();
  }

  // ---------- template helpers ----------

  get progressPct(): number {
    return this.progress.total ? Math.round(this.progress.done / this.progress.total * 100) : 0;
  }

  get resultsPages(): number {
    return Math.ceil(this.resultsTotal / this.resultsLimit);
  }

  get estimatesPages(): number {
    return Math.ceil(this.estimatesTotal / this.estimatesLimit);
  }

  tierLabel(tier: string): string {
    return {
      weight_tag: 'Weight tag',
      duplicate: 'Duplicate SKU',
      prefix: 'SKU family',
      desc_size: 'Description size',
      text: 'Description',
      type: 'Brand+Type',
      none: 'No prediction'
    }[tier] || tier;
  }

  readonly tierKeys = ['weight_tag', 'duplicate', 'prefix', 'desc_size', 'text', 'type'];

  toggleDetail(row: any): void {
    this.detailRow = this.detailRow === row ? null : row;
  }
}
